import { Directive, HostListener, OnDestroy, OnInit, signal, WritableSignal } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Observable, of, Subject } from 'rxjs';
import { filteredIndex, filteredValue, GridHeader, GridPreferences, sortIndex, toQueryFilter } from './grid.dto';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { SubSink } from 'subsink';
import { catchError, debounceTime, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { FORM_DEBOUNCE_TIME } from '../global';
import { BreakpointObserver, Breakpoints, BreakpointState } from '@angular/cdk/layout';
import { isXSmallScreen, matchesScreenSizeMin } from '../main-layout/layout-utils';
import { HIDE_PREVIEW_MENU, MainLayoutComponent, SELECTION_CHANGED, SHOW_PREVIEW_MENU } from '../main-layout/main-layout.component';
import { Dto, MAX_QUERY_RESULTS, Query, Scalar, SearchResult, SearchResultPage } from '@damofire/data-commons';
import { EmitterService } from '../emitter.service';
import { findDtoIndex } from '../forms/form-utilities';
import { StorageService } from '@damofire/data-angular-commons';
import { Sort, SortDirection, SortOption } from './table-sort-options/table-sort-options.component';
import { TranslateService } from '@ngx-translate/core';
import { KeyValue } from '@angular/common';
import { WhereFilterOp } from 'firebase/firestore';
import * as moment from 'moment';

export interface FormValues {
  filter: string;
  status: string;
  [key: string]: Scalar | Scalar[];
}

@Directive()
export abstract class AppGrid<T extends Dto> implements OnInit, OnDestroy {
  private readonly EMPTY_SEARCH_RESULT: SearchResult<T> = {
    page: {
      page: 0,
      size: 0,
      totalPages: 0,
      totalElements: 0,
    },
    result: [],
  };

  searchResult: MatTableDataSource<T> = new MatTableDataSource([]);
  searchPage: SearchResultPage = {};

  viewSubs = new SubSink();

  columnsToDisplay: string[];

  multiSelect = false;
  selection: WritableSignal<T[]> = signal<T[]>([]);
  allSearchResultsSelected = false;
  selectingAllSearchResults = false;
  private selectionAnchor: T | null = null;

  resolvingSearchData = false;
  $search: Subject<boolean> = new Subject();

  itemsPerPageChoices = [25, 50, 75, 100];

  gridPreferences: GridPreferences = {};
  gridForm: UntypedFormGroup;
  currentSort: Sort = null;
  filterSummary = '';

  protected $destroyed: Subject<boolean> = new Subject<boolean>();

  protected constructor(
    protected readonly emitterService: EmitterService,
    protected readonly collection: string,
    protected readonly prefStorageKey: string,
    protected readonly headerColumns: GridHeader[],
    public readonly itemsSortsChoices: SortOption[],
    protected readonly defaultPreferences: GridPreferences,
    protected readonly storage: StorageService,
    protected readonly formBuilder: UntypedFormBuilder,
    protected readonly breakpointObserver: BreakpointObserver,
    protected readonly translate: TranslateService,
  ) {
    this.columnsToDisplay = [];
    this.gridForm = this.createGridForm(this.formBuilder);
  }

  protected getStorageKey(): string {
    return this.prefStorageKey;
  }

  protected getPreferences(reload = false): GridPreferences {
    if (Object.entries(this.gridPreferences).length === 0 || reload) {
      const storageKey = this.getStorageKey();
      if (storageKey) {
        this.gridPreferences = this.storage.getObject(storageKey, this.defaultPreferences);
      }
    }
    return this.gridPreferences;
  }

  ngOnInit(): void {
    const prefs = this.restorePrefs();
    this.updateForm(prefs);
    this.filterSummary = this.updateFilterSummary(this.gridForm.value);

    this.viewSubs.add(
      this.gridForm.valueChanges.pipe(debounceTime(FORM_DEBOUNCE_TIME)).subscribe((value) => {
        this.onFormChanged(value);
        this.saveChangesAndSearch();
      }),
      this.translate.onLangChange.subscribe(() => (this.filterSummary = this.updateFilterSummary(this.gridForm.value))),
    );

    this.viewSubs.add(
      this.$search
        .pipe(
          takeUntil(this.$destroyed),
          switchMap((silent: boolean) => {
            console.log(`Search requested, proceeding. [${this.collection}]`);
            const query: Query = this.createQuery(this.gridForm.value);
            this.prepareForSearch(silent);

            if (query) {
              console.log('Searching data with query.', query);
              const observable = this.executeSearch(query);
              return new Observable<[Query, SearchResult<T>]>((subscriber) => {
                subscriber.add(
                  observable.subscribe((searchResult: SearchResult<T>) => {
                    subscriber.next([query, searchResult]);
                  }),
                );
              });
            }

            return of<[Query, SearchResult<T>]>([query, this.EMPTY_SEARCH_RESULT]);
          }),
          map(([query, data]: [Query, SearchResult<T>]) => {
            const searchResult = this.processResult(data);
            return [query, searchResult] as [Query, SearchResult<T>];
          }),
          map(([query, data]: [Query, SearchResult<T>]) => {
            this.searchResult ??= new MatTableDataSource([]);
            this.searchPage = data.page;
            this.searchResult.data = data.result;

            return [query, this.searchResult] as [Query, MatTableDataSource<T>];
          }),
          tap(([query, data]: [Query, MatTableDataSource<T>]) => this.afterSearch(query)),
          catchError((err) => {
            console.error('Could not complete search', err);
            this.afterSearch();
            return of([null as Query, this.searchResult] as [Query, MatTableDataSource<T>]);
          }),
        )
        .subscribe(([query, data]: [Query, MatTableDataSource<T>]) => this.searchDone(data)),
    );

    this.search();

    const layoutChanges = this.breakpointObserver.observe([Breakpoints.Handset, Breakpoints.Tablet, Breakpoints.Web]);
    this.viewSubs.add(layoutChanges.pipe(filter((r: BreakpointState) => r.matches)).subscribe(() => this.filterColumns()));
  }

  protected onFormChanged(value: { filter: string; status: string }): void {
    this.filterChanged(value.filter);
    this.statusChanged(value.status);

    this.filterSummary = this.updateFilterSummary(this.gridForm.value);
  }

  ngOnDestroy(): void {
    this.searchResult = null;
    this.searchPage = null;
    this.$destroyed.next(true);
  }

  pageOptsUpdated(event: PageEvent): void {
    this.gridPreferences.itemsPerPage = event.pageSize;
    this.gridPreferences.itemsStartsAt = this.gridPreferences.itemsPerPage * event.pageIndex;
    this.saveChangesAndSearch();
  }

  sortDirection(f: string): SortDirection {
    const inx = sortIndex(this.gridPreferences, f);
    if (inx !== -1) {
      return this.gridPreferences.itemsSort[inx].value;
    }

    return null;
  }

  resolveDefaultSort(f: string): SortDirection {
    const matchingSortOption = this.itemsSortsChoices.find((isc: SortOption) => isc.id === f);
    return matchingSortOption?.defaultSort || 'asc';
  }

  toggleSortDirection(f: string, dir?: SortDirection): void {
    const inx = sortIndex(this.gridPreferences, f);
    const matchingSortOption = this.itemsSortsChoices.find((isc: SortOption) => isc.id === f);

    if (inx !== -1) {
      if (dir) {
        this.gridPreferences.itemsSort[inx].value = dir;
      } else {
        this.gridPreferences.itemsSort.splice(inx, 1);
      }
    } else if (dir) {
      this.gridPreferences.itemsSort = [{ key: f, value: dir }];
    }

    if (dir) {
      this.currentSort = { ...matchingSortOption, direction: dir };
    } else {
      this.currentSort = null;
    }
    this.saveChangesAndSearch();
  }

  updateSort(sort: Sort): void {
    if (sort) {
      const inx = sortIndex(this.gridPreferences, sort.id);
      if (inx !== -1) {
        this.gridPreferences.itemsSort[inx].value = sort.direction;
      } else {
        this.gridPreferences.itemsSort = [{ key: sort.id, value: sort.direction }];
      }
    } else {
      this.gridPreferences.itemsSort = [];
    }

    this.currentSort = sort;
    this.saveChangesAndSearch();
  }

  trackGridItems(index: number, item: T): string {
    return item._id.toString();
  }

  search(silent = false): void {
    this.$search.next(silent);
  }

  displayMode(): string {
    return this.gridPreferences.displayMode;
  }

  toggleDisplayMode(): void {
    const displayMode = this.gridPreferences.displayMode;
    this.gridPreferences.displayMode = displayMode === 'grid' ? 'cards' : 'grid';
    this.saveChanges();
  }

  filterChanged(newFilter: string): void {
    this.gridPreferences.filter = newFilter;
    this.gridPreferences.itemsStartsAt = 0;
    this.searchPage.page = 0;
  }

  statusChanged(statusValue: string): void {
    const inx = filteredIndex(this.gridPreferences, 'enabled');

    if (inx !== -1) {
      if (statusValue === 'all') {
        this.gridPreferences.filters.splice(inx, 1);
      } else {
        this.gridPreferences.filters[inx].value = statusValue === 'active';
      }
    } else if (statusValue !== 'all') {
      if (this.gridPreferences.filters) {
        this.gridPreferences.filters.push({ key: 'enabled', value: statusValue === 'active' });
      } else {
        this.gridPreferences.filters = [{ key: 'enabled', value: statusValue === 'active' }];
      }
    }
  }

  clearSelection(): void {
    this.selection.set([]);
    this.selectionAnchor = null;
    this.allSearchResultsSelected = false;
    this.emitSelectionChanged();
  }

  @HostListener('window:keyup.ArrowDown', ['$event'])
  selectNextItem(): void {
    const currentSelection = this.selection();
    if (this.multiSelect || !currentSelection?.length) {
      return;
    }

    const matchingInx = findDtoIndex(this.searchResult.data, currentSelection[0]);
    if (matchingInx === this.searchResult.data.length - 1) {
      return;
    }

    this.updateSelection(this.searchResult.data[matchingInx + 1]);
  }

  @HostListener('window:keyup.ArrowUp', ['$event'])
  selectPreviousItem(): void {
    const currentSelection = this.selection();
    if (this.multiSelect || !currentSelection?.length) {
      return;
    }

    const matchingInx = findDtoIndex(this.searchResult.data, currentSelection[0]);
    if (matchingInx <= 0) {
      return;
    }

    this.updateSelection(this.searchResult.data[matchingInx - 1]);
  }

  updateSelection(newSelection: T): void {
    if (newSelection) {
      this.allSearchResultsSelected = false;
      const currentSelection = this.selection();
      const matchingInx = findDtoIndex(currentSelection, newSelection);
      if (matchingInx === -1) {
        if (this.multiSelect) {
          this.selection.set([...currentSelection, newSelection]);
        } else {
          this.selection.set([newSelection]);
        }

        this.emitSelectionChanged();
      } else {
        if (this.multiSelect) {
          const newArr = currentSelection.filter((_, i) => i !== matchingInx);
          this.selection.set(newArr);
          if (!newArr.length) {
            this.emitterService.get(MainLayoutComponent.channelName()).next({ event: HIDE_PREVIEW_MENU });
          }
          this.emitSelectionChanged();
        } else {
          this.selection.set([newSelection]);
          this.emitSelectionChanged();
          this.emitterService.get(MainLayoutComponent.channelName()).next({ event: SHOW_PREVIEW_MENU, value: true });
        }
      }
    } else {
      this.clearSelection();
    }
  }

  toggleSelectionFromGrid(element: T, event: MouseEvent): void {
    if (!this.multiSelect) {
      this.updateSelection(element);
      return;
    }

    if (event.shiftKey && this.selectionAnchor) {
      const rows = this.searchResult?.data || [];
      const anchorIndex = findDtoIndex(rows, this.selectionAnchor);
      const elementIndex = findDtoIndex(rows, element);

      if (anchorIndex !== -1 && elementIndex !== -1) {
        const start = Math.min(anchorIndex, elementIndex);
        const end = Math.max(anchorIndex, elementIndex);
        const range = rows.slice(start, end + 1);
        const selected = event.ctrlKey || event.metaKey ? [...this.selection()] : [];

        range.forEach((row) => {
          if (findDtoIndex(selected, row) === -1) {
            selected.push(row);
          }
        });

        this.selection.set(selected);
        this.allSearchResultsSelected = false;
        this.emitSelectionChanged();
        return;
      }
    }

    this.selectionAnchor = element;
    this.updateSelection(element);
  }

  togglePageSelection(): void {
    const pageRows = this.searchResult?.data || [];
    if (!pageRows.length) {
      return;
    }

    const pageIsSelected = this.isPageFullySelected();
    const pageIds = new Set(pageRows.map((row) => row._id));
    const remainingSelection = this.selection().filter((row) => !pageIds.has(row._id));
    const nextSelection = pageIsSelected ? remainingSelection : [...remainingSelection, ...pageRows];

    this.selection.set(nextSelection);
    this.selectionAnchor = pageIsSelected ? null : pageRows[pageRows.length - 1];
    this.emitSelectionChanged();
  }

  isPageFullySelected(): boolean {
    const pageRows = this.searchResult?.data || [];
    return pageRows.length > 0 && pageRows.every((row) => this.isSelected(row));
  }

  isPagePartiallySelected(): boolean {
    const pageRows = this.searchResult?.data || [];
    const selectedRows = pageRows.filter((row) => this.isSelected(row));
    return selectedRows.length > 0 && selectedRows.length < pageRows.length;
  }

  isSelected(element: T): boolean {
    return findDtoIndex(this.selection(), element) !== -1;
  }

  toggleMultiSelectionMode(value?: boolean): void {
    this.multiSelect = value !== undefined ? value : !this.multiSelect;
    this.filterColumns();
    if (!this.multiSelect) {
      this.clearSelection();
    }
  }

  toggleAllSearchResults(checked: boolean): void {
    if (checked) {
      this.selectAllSearchResults();
      return;
    }

    this.clearSelection();
  }

  canSelectAllSearchResults(): boolean {
    const totalElements = this.searchPage?.totalElements || 0;
    const visibleElements = this.searchResult?.data?.length || 0;
    return totalElements > visibleElements;
  }

  selectAllSearchResults(): void {
    if (!this.canSelectAllSearchResults() || this.selectingAllSearchResults) {
      return;
    }

    this.selectingAllSearchResults = true;
    const query = this.createQuery(this.gridForm.value);

    this.viewSubs.add(
      this.executeSearch({ ...query, from: 0, size: MAX_QUERY_RESULTS })
        .pipe(takeUntil(this.$destroyed))
        .subscribe({
          next: (result) => {
            this.selection.set([...(result.result || [])]);
            this.selectionAnchor = null;
            this.allSearchResultsSelected = true;
            this.selectingAllSearchResults = false;
            this.emitSelectionChanged();
          },
          error: (error) => {
            this.selectingAllSearchResults = false;
            console.error('Could not select all search results', error);
          },
        }),
    );
  }

  saveChangesAndSearch(): void {
    if (this.saveChanges()) {
      this.search();
    }
  }

  saveChanges(): boolean {
    if (this.gridForm.valid) {
      console.log(`Saving grid preferences`, this.gridPreferences);
      this.storage.setObject(this.getStorageKey(), this.gridPreferences);
    }

    return this.gridForm.valid;
  }

  shouldDisplayCardsView(): boolean {
    return this.gridPreferences.displayMode === 'cards' || isXSmallScreen(this.breakpointObserver);
  }

  isXSmallScreen(): boolean {
    return isXSmallScreen(this.breakpointObserver);
  }

  protected createGridForm(formBuilder: UntypedFormBuilder): UntypedFormGroup {
    return formBuilder.group({
      filter: ['', Validators.maxLength(50)],
      status: [''],
      displayMode: [''],
    });
  }

  protected restorePrefs(reload = false): GridPreferences {
    const preferences = this.getPreferences(reload);

    if (preferences.itemsSort?.length) {
      const sortOptionId = preferences.itemsSort[0].key;
      const matchingOption = this.itemsSortsChoices.find((isc) => isc.id === sortOptionId);
      this.currentSort = { ...matchingOption, direction: preferences.itemsSort[0].value };
    }

    return preferences;
  }

  protected updateForm(gridPreferences: GridPreferences): void {
    const statusFilteredValue = filteredValue(gridPreferences, 'enabled');
    this.gridForm.controls['filter'].setValue(gridPreferences?.filter || '');
    this.gridForm.controls['status'].setValue(statusFilteredValue ? 'active' : statusFilteredValue !== null ? 'inactive' : 'all');
  }

  protected searchDone(result: MatTableDataSource<T>): void {
    console.log(`Search done. [${result.data.length} results.]`);
  }

  protected prepareForSearch(silent = false): void {
    this.resolvingSearchData = !silent;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected afterSearch(query?: Query): void {
    this.resolvingSearchData = false;
  }

  protected abstract executeSearch(query: Query): Observable<SearchResult<T>>;

  protected processResult(data: SearchResult<T>): SearchResult<T> {
    console.log(`Search result came in. [${this.collection}]`);

    if (data.result) {
      this.mapSearchResult(data.result);
    } else {
      console.log(`Search came back empty. [${this.collection}]`);
    }

    return data;
  }

  protected createQuery(formValue: FormValues): Query {
    const query: Query = {
      searchPhrase: this.gridPreferences.filter || null,
      from: this.gridPreferences.itemsStartsAt,
      size: this.gridPreferences.itemsPerPage,
    };

    query.sort = this.toQuerySort(this.gridPreferences);
    query.filters = toQueryFilter(this.gridPreferences);
    return query;
  }

  protected mapSearchResult(result: T[]): T[] {
    const currentSelection = this.selection();
    let selectionUpdated = false;

    result.forEach((r) => {
      const selectionInx = findDtoIndex(currentSelection, r);
      if (selectionInx > -1) {
        currentSelection[selectionInx] = r;
        selectionUpdated = true;
      }
    });

    if (selectionUpdated) {
      this.selection.set([...currentSelection]);
    }

    return result;
  }

  filterColumns(): void {
    const tmpColumns: string[] = [];
    this.headerColumns.forEach((col) => {
      if (matchesScreenSizeMin(col.minScreenSize, this.breakpointObserver)) {
        tmpColumns.push(col.name);
      }
    });

    this.columnsToDisplay = this.multiSelect ? ['select', ...tmpColumns] : tmpColumns;
  }

  private emitSelectionChanged(): void {
    this.selectionChanged();
    this.emitterService.get(MainLayoutComponent.channelName()).next({ event: SELECTION_CHANGED, value: this.selection() });
  }

  protected selectionChanged(): void {
    // Selection persistence is implemented by grids that need it.
  }

  protected toQuerySort(prefs: GridPreferences): Record<string, SortDirection> {
    const sort: Record<string, SortDirection> = {};
    if (prefs.itemsSort) {
      prefs.itemsSort.forEach((s) => {
        sort[s.key] = s.value;
      });
    }
    return sort;
  }

  protected getSearchResult(): MatTableDataSource<T> {
    return this.searchResult;
  }

  protected updatePreferences(value: unknown, key: string): void {
    if (!this.gridPreferences.filters) {
      this.gridPreferences.filters = [];
    }

    if ((Array.isArray(value) && value.length) || (!Array.isArray(value) && value) || value === 0) {
      const pref = this.gridPreferences.filters.find((f) => f.key === key);
      if (!pref) {
        this.gridPreferences.filters.push({ key, value });
      } else {
        pref.value = value;
      }
    } else {
      this.gridPreferences.filters = this.gridPreferences.filters.filter((f) => f.key !== key);
    }
  }

  clearSummaryFilter(): void {
    // N/A
  }

  protected updateFilterSummary(formValue: FormValues): string {
    return '';
  }

  protected queryToPrefs(query: Query): GridPreferences {
    const { searchPhrase = null, from, size, filters, filtersOps, sort } = query;
    const filterKeys = Object.keys(filters);
    const sortKeys = Object.keys(sort);

    const filtersPref: KeyValue<string, unknown>[] = filterKeys.map<KeyValue<string, unknown>>((key) => ({ key, value: filters[key] }));
    filtersOps?.forEach((filtersOp) => filtersPref.push({ key: filtersOp.field, value: filtersOp.value }));

    return {
      displayMode: this.displayMode(),
      filter: searchPhrase,
      itemsStartsAt: from,
      itemsPerPage: size,
      filters: filtersPref,
      itemsSort: sortKeys.map<KeyValue<string, SortDirection>>((key) => ({ key, value: sort[key] })),
    };
  }

  protected convertDateFilter(query: Query, filterField: string, field: string, op: WhereFilterOp): void {
    if (query.filters[filterField]) {
      query.filtersOps ??= [];
      const momentDate = moment(query.filters[filterField] as string);
      const rangeMomentDate = op === '<=' ? momentDate.endOf('day') : momentDate.startOf('day');
      query.filtersOps.push({ field, op, value: rangeMomentDate.toDate() });
      delete query.filters[filterField];
    }
  }

  protected convertMultiSelectFilter(query: Query, filterField: string, field = filterField): void {
    if (query.filters[filterField]) {
      query.filtersOps ??= [];
      query.filtersOps.push({ field, op: 'in', value: query.filters[filterField] });
      delete query.filters[filterField];
    }
  }
}
